/**
 * How a formula is PERSISTED: an AST in JSON, keyed by ids, never the text the user typed.
 *
 * The formula language is a user-interface surface — for people and for agents — and names belong
 * to that surface only. Storing structure instead of text buys three things:
 *
 *   - a grammar change costs no data migration, because nothing stored spells the grammar;
 *   - renaming a document, sheet or column cannot break a stored formula, since only ids are kept;
 *   - loading skips a parse, so `cellRuntime`/`engine` decode instead of re-lexing on every read.
 *
 * The keys are short because this sits inside a `jsonb` blob that holds one entry per formula of
 * every grid; the shape is small enough that the terseness costs no clarity.
 *
 * `raw` is the reason this module never throws. Anything it cannot make sense of — a value written
 * by a newer version, a hand-edited blob, text the parser rejects — decodes to a `raw` node that
 * keeps the original verbatim. A broken formula then behaves like a broken formula (the cell shows
 * an error code) instead of vanishing, and the user can still open it and fix it.
 */

import { parseCanonical, type AstNode, type BinaryOperator, type FormulaRowReference } from './language'

/** The stored counterpart of `AstNode`, minus every span: source positions describe text that no
 *  longer exists once the formula is structure. */
export type StoredNode = (
  | { k: 'num'; t: string }
  | { k: 'txt'; s: string }
  | { k: 'call'; n: string; a: StoredNode[] }
  | { k: 'ref'; f: string; r?: StoredRow; s?: string; d?: string }
  | { k: 'range'; a: StoredRef; b: StoredRef }
  | { k: 'un'; o: '+' | '-'; a: StoredNode }
  | { k: 'bin'; o: BinaryOperator; l: StoredNode; r: StoredNode }
  | { k: 'pct'; a: StoredNode }
  | { k: 'grp'; a: StoredNode }
  | { k: 'name'; n: string }
  | { k: 'raw'; t: string }
) & { nl?: boolean }

export type StoredRef = Extract<StoredNode, { k: 'ref' }>
export type StoredRow = 'column' | 'current' | { id: string }

/** The persisted envelope. Versioned so a future shape can be told apart from this one on sight
 *  — which is exactly what lets `fromStored` refuse it safely instead of guessing. */
export interface StoredFormula {
  v: 3
  ast: StoredNode
}

export const STORED_VERSION = 3 as const

/**
 * Canonical TEXT → the stored form, for the two places text is still the input: formulas authored
 * in the application's own code (`formulaDefaults`), and the v2 upgrade in `migrateV2.ts`.
 * Text that does not parse becomes a `raw` formula rather than an exception.
 */
export function storedFromCanonical(canonical: string): StoredFormula {
  const parsed = parseCanonical(canonical)
  return parsed.ok ? toStored(parsed.ast) : rawFormula(canonical)
}

/** Wraps text that does not parse, so it survives a save and can be repaired later. */
export function rawFormula(text: string): StoredFormula {
  return { v: STORED_VERSION, ast: { k: 'raw', t: text } }
}

export const isRawFormula = (formula: StoredFormula): boolean => formula.ast.k === 'raw'

// ── Encoding ──────────────────────────────────────────────────────────────────────────────────

function encode(node: AstNode): StoredNode {
  const encoded = encodeInner(node)
  if (node.nl) encoded.nl = true
  return encoded
}

function encodeInner(node: AstNode): StoredNode {
  switch (node.kind) {
    // `n` is deliberately NOT stored: it is `Number(text)`, and keeping one copy is what stops the
    // two from ever disagreeing.
    case 'number': return { k: 'num', t: node.text }
    case 'text': return { k: 'txt', s: node.s }
    case 'call': return { k: 'call', n: node.name, a: node.args.map(encode) }
    case 'ref': return encodeRef(node)
    case 'range': return { k: 'range', a: encodeRef(node.from), b: encodeRef(node.to) }
    case 'unary': return { k: 'un', o: node.op, a: encode(node.arg) }
    case 'binary': return { k: 'bin', o: node.op, l: encode(node.left), r: encode(node.right) }
    case 'percent': return { k: 'pct', a: encode(node.arg) }
    case 'group': return { k: 'grp', a: encode(node.arg) }
    case 'raw': return { k: 'raw', t: node.text }
    case 'name': return { k: 'name', n: node.name }
  }
}

function encodeRef(node: Extract<AstNode, { kind: 'ref' }>): StoredRef {
  return {
    k: 'ref',
    f: node.field,
    ...(node.row === undefined ? {} : { r: node.row }),
    ...(node.sheetId === undefined ? {} : { s: node.sheetId }),
    ...(node.documentId === undefined ? {} : { d: node.documentId }),
    ...(node.nl ? { nl: true } : {}),
  }
}

/** AST → the value written to the database. */
export function toStored(ast: AstNode): StoredFormula {
  return { v: STORED_VERSION, ast: encode(ast) }
}

// ── Decoding ──────────────────────────────────────────────────────────────────────────────────

/** Decoded nodes describe no source text, so every span is empty. Nothing reads a span off a
 *  stored formula — the editor only underlines what the user is currently typing. */
const NO_SPAN = { start: 0, end: 0 }

const OPERATORS = new Set<string>(['+', '-', '*', '/', '^', '&', '=', '<>', '<', '>', '<=', '>='])

class DecodeFailure extends Error {}

const bad = (): never => { throw new DecodeFailure() }

const obj = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : bad()

function decodeRow(value: unknown): FormulaRowReference {
  if (value === 'column' || value === 'current') return value
  const record = obj(value)
  return typeof record.id === 'string' ? { id: record.id } : bad()
}

function decodeRef(value: unknown): Extract<AstNode, { kind: 'ref' }> {
  const node = obj(value)
  if (node.k !== 'ref' || typeof node.f !== 'string') bad()
  return {
    kind: 'ref',
    field: node.f as string,
    ...(node.r === undefined ? {} : { row: decodeRow(node.r) }),
    ...(node.s === undefined ? {} : { sheetId: typeof node.s === 'string' ? node.s : bad() }),
    ...(node.d === undefined ? {} : { documentId: typeof node.d === 'string' ? node.d : bad() }),
    ...(node.nl === true ? { nl: true } : {}),
    ...NO_SPAN,
  }
}

function decode(value: unknown): AstNode {
  const node = obj(value)
  const decoded = decodeInner(node)
  if (node.nl === true) decoded.nl = true
  return decoded
}

function decodeInner(node: Record<string, unknown>): AstNode {
  switch (node.k) {
    case 'num': {
      if (typeof node.t !== 'string') bad()
      const n = Number(node.t)
      if (!Number.isFinite(n)) bad()
      return { kind: 'number', n, text: node.t as string, ...NO_SPAN }
    }
    case 'txt':
      return typeof node.s === 'string' ? { kind: 'text', s: node.s, ...NO_SPAN } : bad()
    case 'call':
      if (typeof node.n !== 'string' || !Array.isArray(node.a)) bad()
      return { kind: 'call', name: node.n as string, args: (node.a as unknown[]).map(decode), ...NO_SPAN }
    case 'ref':
      return decodeRef(node)
    case 'range':
      return { kind: 'range', from: decodeRef(node.a), to: decodeRef(node.b), ...NO_SPAN }
    case 'un':
      if (node.o !== '+' && node.o !== '-') bad()
      return { kind: 'unary', op: node.o as '+' | '-', arg: decode(node.a), ...NO_SPAN }
    case 'bin':
      if (typeof node.o !== 'string' || !OPERATORS.has(node.o)) bad()
      return { kind: 'binary', op: node.o as BinaryOperator, left: decode(node.l), right: decode(node.r), ...NO_SPAN }
    case 'pct':
      return { kind: 'percent', arg: decode(node.a), ...NO_SPAN }
    case 'grp':
      return { kind: 'group', arg: decode(node.a), ...NO_SPAN }
    case 'name':
      return typeof node.n === 'string' ? { kind: 'name', name: node.n, ...NO_SPAN } : bad()
    case 'raw':
      return typeof node.t === 'string' ? { kind: 'raw', text: node.t, ...NO_SPAN } : bad()
    default:
      return bad()
  }
}

/**
 * The database value → an AST, always. A value this module cannot read becomes a `raw` node
 * holding a printable version of it, so the formula is still visible and still editable rather
 * than silently discarded.
 */
export function fromStored(value: unknown): AstNode {
  try {
    const envelope = obj(value)
    if (envelope.v !== STORED_VERSION) bad()
    return decode(envelope.ast)
  } catch (e) {
    if (!(e instanceof DecodeFailure)) throw e
    return { kind: 'raw', text: describe(value), ...NO_SPAN }
  }
}

/** Whether a value is a well-formed stored formula, without keeping the decoded result. */
export function isStoredFormula(value: unknown): value is StoredFormula {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  if ((value as Record<string, unknown>).v !== STORED_VERSION) return false
  try {
    decode((value as Record<string, unknown>).ast)
    return true
  } catch (e) {
    if (!(e instanceof DecodeFailure)) throw e
    return false
  }
}

/** The best printable form of something unreadable, for the user to see and repair. */
function describe(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? ''
  } catch {
    return ''
  }
}
